import { Component } from '@theme/component';

/**
 * Announcement banner with fade rotation and offer countdowns.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * @type {number|undefined}
   */
  #countdownInterval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.#restoreDismissState();
    this.#initCountdowns();
    this.#bindDismiss();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    this.suspend();
    if (this.#countdownInterval) clearInterval(this.#countdownInterval);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    super.disconnectedCallback?.();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.#visibleSlides;
    if (!slides.length) return;

    this.#current = current;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  get #visibleSlides() {
    return (this.refs.slides ?? []).filter(
      (slide) => !slide.hasAttribute('hidden') && !slide.classList.contains('is-expired-hidden')
    );
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #bindDismiss() {
    const root = this.closest('[data-announcement-bar]');
    const button = root?.querySelector('[data-announcement-dismiss]');
    if (!root || !button) return;

    button.addEventListener('click', () => {
      const key = root.getAttribute('data-dismiss-key');
      if (key) {
        try {
          sessionStorage.setItem(key, '1');
        } catch {
          /* ignore */
        }
      }
      root.hidden = true;
      root.classList.add('is-dismissed');
    });
  }

  #restoreDismissState() {
    const root = this.closest('[data-announcement-bar]');
    if (!root) return;
    const key = root.getAttribute('data-dismiss-key');
    if (!key) return;
    try {
      if (sessionStorage.getItem(key) === '1') {
        root.hidden = true;
        root.classList.add('is-dismissed');
      }
    } catch {
      /* ignore */
    }
  }

  #initCountdowns() {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    const tick = () => {
      slides.forEach((slide) => this.#updateCountdown(slide));
      this.#ensureActiveVisibleSlide();
    };

    tick();
    this.#countdownInterval = setInterval(tick, 1000);
  }

  /**
   * @param {HTMLElement} slide
   */
  #updateCountdown(slide) {
    const endRaw = slide.getAttribute('data-countdown-end');
    if (!endRaw) return;

    const end = this.#parseEndDate(endRaw);
    const countdown = slide.querySelector('[data-announcement-countdown]');
    if (!end || !countdown) return;

    const diff = end.getTime() - Date.now();
    const hideWhenExpired = slide.getAttribute('data-hide-when-expired') === 'true';

    if (diff <= 0) {
      countdown.classList.add('is-expired');
      const expired = countdown.querySelector('.announcement-bar__expired');
      if (expired) expired.hidden = false;

      if (hideWhenExpired) {
        slide.classList.add('is-expired', 'is-expired-hidden');
        slide.setAttribute('hidden', '');
        slide.setAttribute('aria-hidden', 'true');
      } else {
        slide.classList.add('is-expired');
      }
      return;
    }

    slide.classList.remove('is-expired', 'is-expired-hidden');
    slide.removeAttribute('hidden');
    countdown.classList.remove('is-expired');

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#setUnit(countdown, '[data-days]', days);
    this.#setUnit(countdown, '[data-hours]', hours);
    this.#setUnit(countdown, '[data-minutes]', minutes);
    this.#setUnit(countdown, '[data-seconds]', seconds);
  }

  /**
   * @param {HTMLElement} root
   * @param {string} selector
   * @param {number} value
   */
  #setUnit(root, selector, value) {
    const el = root.querySelector(selector);
    if (!el) return;
    el.textContent = String(value).padStart(2, '0');
  }

  /**
   * @param {string} value
   */
  #parseEndDate(value) {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Support "YYYY-MM-DD", "YYYY-MM-DD HH:MM", "YYYY-MM-DD HH:MM:SS", ISO
    const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  }

  #ensureActiveVisibleSlide() {
    const slides = this.#visibleSlides;
    const root = this.closest('[data-announcement-bar]');

    if (!slides.length) {
      if (root) root.hidden = true;
      this.suspend();
      return;
    }

    if (root?.classList.contains('is-dismissed')) return;
    if (root) root.hidden = false;

    const activeVisible = slides.some((slide) => slide.getAttribute('aria-hidden') === 'false');
    if (!activeVisible) {
      this.current = 0;
    }
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
